import { useContext } from 'react';
import { ProductsContext } from 'context-providers/products-provider';
import {
  ExperimentFlag,
  useFeatureFlags,
} from 'context-providers/feature-flag-provider';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import { getSrpTermMonths } from 'lib/srp';
import { getLocalizedPrice } from 'lib/utils/get-localized-price';

const ANCHORED_SKUS: string[] = [
  PRODUCT_SKUS.DAILY_SYNBIOTIC,
  PRODUCT_SKUS.DAILY_SYNBIOTIC_3MO,
  PRODUCT_SKUS.DAILY_SYNBIOTIC_6MO,
];

export const NO_SAVINGS = { perLine: 0, perYear: 0 };

// The gap between a DS-01 subscription line and the one-time price, shown as
// extra savings. Display-only; totals are unchanged.
export const getOneTimeSavings = (
  oneTimePrice: number,
  sku: string | null | undefined,
  quantity: number,
  lineSubtotal: number,
): { perLine: number; perYear: number } => {
  if (!sku || !ANCHORED_SKUS.includes(sku)) return NO_SAVINGS;
  const units = (getSrpTermMonths(sku) ?? 1) * quantity;
  const perLine = Math.max(
    0,
    Math.round((oneTimePrice * units - lineSubtotal) * 100) / 100,
  );
  return perLine ? { perLine, perYear: (perLine / units) * 12 } : NO_SAVINGS;
};

type OtpBuybox =
  | { isActive: true; oneTimePrice: number }
  | { isActive: false; oneTimePrice: number | null };

// oneTimePrice is null until the one-time plan resolves, since the cart skips
// SKUs it can't find. Inactive while the catalog's currency lags the caller's.
export const useOtpBuybox = (currency: string): OtpBuybox => {
  const { experimentFlags } = useFeatureFlags();
  const { getProduct, currencyCode } = useContext(ProductsContext);
  const oneTimeProduct = getProduct(PRODUCT_SKUS.DAILY_SYNBIOTIC_ONE_TIME);
  const price = parseFloat(getLocalizedPrice(oneTimeProduct) ?? '');
  const oneTimePrice =
    oneTimeProduct?.shopifyData?.id && price > 0 ? price : null;

  return experimentFlags[ExperimentFlag.Ds01OtpToggleBuybox] &&
    currencyCode === currency &&
    oneTimePrice
    ? { isActive: true, oneTimePrice }
    : { isActive: false, oneTimePrice };
};
